import { useEffect, useRef } from "react";

const DEFAULT_TIMEOUT_MINUTES = 20;

/**
 * Logs the user out after `timeoutMinutes` of no mouse/keyboard/touch/scroll
 * activity. Pass `enabled = false` to disable entirely. The timer is reset on
 * any user interaction so active users are never interrupted.
 */
export function useInactivityTimeout(
  onTimeout: () => void,
  enabled: boolean = true,
  timeoutMinutes: number = DEFAULT_TIMEOUT_MINUTES
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    const timeoutMs = Math.max(1, timeoutMinutes) * 60 * 1000;

    function reset() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onTimeoutRef.current();
      }, timeoutMs);
    }

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset(); // start immediately

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, timeoutMinutes]);
}
